import { useId, useState, type KeyboardEvent } from 'react';
import { formStyles } from '@utils/formStyles';

export interface ValueSetInputProps {
    /** Field label rendered above the chip box. */
    label?: string;
    /** Committed numeric values, kept sorted ascending by the component. */
    values: number[];
    onChange: (values: number[]) => void;
    placeholder?: string;
    helpText?: string;
    /** Suffix shown on each chip, e.g. '%' or '$'. */
    unit?: string;
    /** Lower bound. Combined with minExclusive to express "positive" vs "non-negative". */
    min?: number;
    /** When true the value must be strictly greater than min. */
    minExclusive?: boolean;
    max?: number;
    integer?: boolean;
    disabled?: boolean;
    /** Externally supplied error (e.g. from a form plan). */
    error?: string;
    /** Show the Range… generator affordance (default true). */
    allowRange?: boolean;
    id?: string;
}

const MAX_GENERATED_VALUES = 200;
const RANGE_EPSILON = 1e-9;

const roundValue = (value: number): number => Math.round(value * 1e6) / 1e6;

const formatValue = (value: number): string => String(roundValue(value));

const splitTokens = (raw: string): string[] => raw.split(/[\s,]+/).filter((token) => token.length > 0);

const ValueSetInput = ({
    label,
    values,
    onChange,
    placeholder = 'Type a number, press Enter',
    helpText,
    unit,
    min,
    minExclusive = false,
    max,
    integer = false,
    disabled = false,
    error,
    allowRange = true,
    id
}: ValueSetInputProps) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const [draft, setDraft] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isRangeOpen, setIsRangeOpen] = useState(false);
    const [rangeFrom, setRangeFrom] = useState('');
    const [rangeTo, setRangeTo] = useState('');
    const [rangeStep, setRangeStep] = useState('');

    const validateValue = (value: number): string | null => {
        if (!Number.isFinite(value)) return 'must be a number';
        if (integer && !Number.isInteger(value)) return 'must be a whole number';

        if (min !== undefined) {
            if (minExclusive && value <= min) return `must be greater than ${min}`;
            if (!minExclusive && value < min) return `must be at least ${min}`;
        }

        if (max !== undefined && value > max) return `must be at most ${max}`;

        return null;
    };

    const addTokens = (tokens: string[]): boolean => {
        const seen = new Set(values.map(roundValue));
        const next = [...values];
        let firstError: string | null = null;

        for (const token of tokens) {
            const parsed = Number(token);
            const validationError = validateValue(parsed);

            if (validationError) {
                firstError = firstError ?? `"${token}" ${validationError}`;
                continue;
            }

            const rounded = roundValue(parsed);

            if (seen.has(rounded)) continue;

            seen.add(rounded);
            next.push(rounded);
        }

        if (next.length !== values.length) {
            onChange([...next].sort((left, right) => left - right));
        }

        setLocalError(firstError);

        return firstError === null;
    };

    const removeValue = (value: number) => {
        onChange(values.filter((existing) => existing !== value));
        setLocalError(null);
    };

    const commitDraft = () => {
        const tokens = splitTokens(draft);

        if (tokens.length === 0) {
            setDraft('');
            return;
        }

        if (addTokens(tokens)) setDraft('');
    };

    const handleDraftChange = (rawValue: string) => {
        if (/[\s,]/.test(rawValue)) {
            const tokens = splitTokens(rawValue);
            const trailing = /[\s,]$/.test(rawValue) ? '' : (tokens.pop() ?? '');

            if (tokens.length > 0) addTokens(tokens);
            setDraft(trailing);
            return;
        }

        setDraft(rawValue);
        if (localError) setLocalError(null);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitDraft();
            return;
        }

        if (event.key === 'Backspace' && draft === '' && values.length > 0) {
            event.preventDefault();
            removeValue(values[values.length - 1]);
        }
    };

    const generateRange = () => {
        const from = Number(rangeFrom);
        const to = Number(rangeTo);
        const step = Number(rangeStep);

        if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(step)) {
            setLocalError('Range needs numeric from, to, and step.');
            return;
        }

        if (step <= 0) {
            setLocalError('Range step must be greater than 0.');
            return;
        }

        if (from > to) {
            setLocalError('Range "from" must be less than or equal to "to".');
            return;
        }

        const generated: string[] = [];

        for (let current = from; current <= to + RANGE_EPSILON && generated.length < MAX_GENERATED_VALUES; current += step) {
            generated.push(String(roundValue(current)));
        }

        if (addTokens(generated)) {
            setIsRangeOpen(false);
            setRangeFrom('');
            setRangeTo('');
            setRangeStep('');
        }
    };

    const displayError = error ?? localError;
    const borderClass = displayError ? 'border-red-500' : isFocused ? 'border-[#888]' : 'border-[#444]';

    return (
        <div className="w-full">
            {(label || allowRange) && (
                <div className="mb-1 flex items-center justify-between gap-2">
                    {label ? (
                        <label className={`${formStyles.label} mb-0`} htmlFor={inputId}>
                            {label}
                        </label>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-2">
                        {values.length > 0 && (
                            <span className="text-xs text-zinc-500">
                                {values.length} value{values.length === 1 ? '' : 's'}
                            </span>
                        )}
                        {allowRange && !disabled && (
                            <button
                                className="text-xs font-medium text-cyan-400 hover:text-cyan-300"
                                type="button"
                                onClick={() => setIsRangeOpen((open) => !open)}
                            >
                                {isRangeOpen ? 'Close range' : 'Range…'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div
                className={`flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-md border bg-[#1E1E1E] px-2 py-1.5 transition-colors ${borderClass} ${
                    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-text'
                }`}
                role="presentation"
                onClick={() => !disabled && document.getElementById(inputId)?.focus()}
            >
                {values.map((value) => (
                    <span
                        className="inline-flex items-center gap-1 rounded bg-zinc-700/70 py-0.5 pl-2 pr-1 text-sm text-zinc-100"
                        key={value}
                    >
                        {formatValue(value)}
                        {unit}
                        {!disabled && (
                            <button
                                aria-label={`Remove ${formatValue(value)}${unit ?? ''}`}
                                className="flex h-4 w-4 items-center justify-center rounded text-zinc-400 hover:bg-zinc-600 hover:text-red-300"
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    removeValue(value);
                                }}
                            >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} />
                                </svg>
                            </button>
                        )}
                    </span>
                ))}
                <input
                    className="h-6 min-w-[90px] flex-1 border-0 bg-transparent p-0.5 text-sm text-zinc-200 placeholder-[#7c8083] outline-none"
                    disabled={disabled}
                    id={inputId}
                    inputMode="decimal"
                    placeholder={values.length === 0 ? placeholder : 'Add another…'}
                    value={draft}
                    onBlur={() => {
                        setIsFocused(false);
                        commitDraft();
                    }}
                    onChange={(event) => handleDraftChange(event.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={handleKeyDown}
                />
            </div>

            {isRangeOpen && (
                <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-zinc-800 bg-black/30 p-2.5">
                    <RangeField label="From" value={rangeFrom} onChange={setRangeFrom} />
                    <RangeField label="To" value={rangeTo} onChange={setRangeTo} />
                    <RangeField label="Step" value={rangeStep} onChange={setRangeStep} />
                    <button
                        className="h-[34px] rounded bg-cyan-600 px-3 text-sm font-medium text-white hover:bg-cyan-700"
                        type="button"
                        onClick={generateRange}
                    >
                        Generate
                    </button>
                    <span className="pb-2 text-xs text-zinc-500">Builds evenly spaced values you can still edit.</span>
                </div>
            )}

            {helpText && !displayError && <div className={formStyles.helpText}>{helpText}</div>}
            {displayError && <div className={formStyles.errorText}>{displayError}</div>}
        </div>
    );
};

const RangeField = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => {
    const fieldId = useId();

    return (
        <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400" htmlFor={fieldId}>
                {label}
            </label>
            <input
                className="h-[34px] w-20 rounded-md border border-[#444] bg-[#1E1E1E] px-2 text-sm text-zinc-200 outline-none focus:border-[#888]"
                id={fieldId}
                inputMode="decimal"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </div>
    );
};

export default ValueSetInput;
